import { FHIR_UNITS, fhirWeightToLb, kgToLb, lbToKg } from "@vitallhealth/fhir-core";

import type { WeightParameters } from "./goal-utils";

/**
 * Weight display helpers. The conversion maths (UCUM unit handling, the exact
 * lb/kg factor, canonical rounding) comes from @vitallhealth/fhir-core so the
 * dashboard, patient app and voice agent agree. This module only adds what
 * fhir-core leaves to consumers: the user's display unit, formatting, and
 * converting threshold values for editing.
 *
 * Storage is canonical lb: weight Goals are written in lb whatever unit they
 * carried, and readings are compared in lb. Values are converted for display
 * and back to lb on save.
 */

/**
 * A display unit: fhir-core's FHIR unit text, "kg" or "lb", which is also what
 * the weight_unit preference stores. fhir-core exports the values, not a type.
 */
export type WeightUnit = typeof FHIR_UNITS.KG | typeof FHIR_UNITS.LBS;

/** The selectable units, in dropdown order. */
export const WEIGHT_UNITS: readonly WeightUnit[] = [FHIR_UNITS.KG, FHIR_UNITS.LBS];

/** Unit used until preferences load, or when the stored value is not recognised. */
export const DEFAULT_WEIGHT_UNIT: WeightUnit = FHIR_UNITS.LBS;

export const isWeightUnit = (value: unknown): value is WeightUnit => WEIGHT_UNITS.includes(value as WeightUnit);

/**
 * Translation key for a unit's label (e.g. "lbs", "kg"), under common.units.weight
 * in the locale file. Pass it to translate() wherever a weight is labelled.
 */
export const weightUnitLabelKey = (unit: WeightUnit): string => `common.units.weight.${unit}`;
const roundTo = (value: number, decimals: number): number => Number(value.toFixed(decimals));

/**
 * Decimal places a weight threshold is entered and displayed with, per unit.
 * lb keeps the one decimal the form has always had; kg gets two so a stored lb
 * value converts to a kg value the form will accept back unchanged.
 */
export const WEIGHT_THRESHOLD_DECIMALS: Record<WeightUnit, number> = {
  [FHIR_UNITS.LBS]: 1,
  [FHIR_UNITS.KG]: 2,
};

/** Upper bound for the weight max-delta threshold, in the stored unit (lb). */
export const WEIGHT_MAX_DELTA_LB = 5;

/**
 * Upper bound for the weight max-delta threshold, per display unit. The kg bound
 * is the lb bound converted and rounded to the kg precision (2.27 kg), so a
 * stored 5 lb delta displays within the bound and can be saved untouched.
 */
export const WEIGHT_MAX_DELTA: Record<WeightUnit, number> = {
  [FHIR_UNITS.LBS]: WEIGHT_MAX_DELTA_LB,
  [FHIR_UNITS.KG]: roundTo(lbToKg(WEIGHT_MAX_DELTA_LB), WEIGHT_THRESHOLD_DECIMALS[FHIR_UNITS.KG]),
};

/** A FHIR weight quantity's `value`, `unit` and UCUM `code`. */
type WeightQuantity = { value?: number; unit?: string; code?: string };

/**
 * What one of each unit converts to through fhir-core: 1 lb, and 2.2 lb for a
 * kilogram at fhir-core's two-decimal canonical precision. fhir-core keeps the
 * accepted unit spellings private and only exports the conversion, so the
 * stored unit is recovered from this instead of a second spelling table that
 * could drift from fhir-core's.
 */
const LB_PER_STORED_UNIT: Record<WeightUnit, number | null> = {
  [FHIR_UNITS.LBS]: fhirWeightToLb(1, FHIR_UNITS.LBS),
  [FHIR_UNITS.KG]: fhirWeightToLb(1, FHIR_UNITS.KG),
};

/**
 * The unit a FHIR weight quantity is stored in: lb or kg, or undefined when the
 * unit is missing or anything else.
 */
export const storedWeightUnit = (quantity: Omit<WeightQuantity, "value"> | undefined): WeightUnit | undefined => {
  const lbPerUnit = fhirWeightToLb(1, quantity?.unit, quantity?.code);
  if (lbPerUnit === null) {
    return undefined;
  }
  return WEIGHT_UNITS.find(unit => LB_PER_STORED_UNIT[unit] === lbPerUnit);
};

/**
 * A FHIR weight quantity in canonical lb, or undefined when it has no value. A
 * missing or unrecognised unit is treated as lb: fhir-core returns null for
 * those, and the dashboard has always shown such readings as lb.
 */
export const readingToLb = (quantity: WeightQuantity | undefined): number | undefined => {
  if (quantity?.value == null) {
    return undefined;
  }
  return fhirWeightToLb(quantity.value, quantity.unit, quantity.code) ?? quantity.value;
};

/** Convert an lb value to the display unit (unrounded). */
export const lbToUnit = (lb: number, unit: WeightUnit): number => (unit === FHIR_UNITS.KG ? lbToKg(lb) : lb);

/** Convert a display-unit value to lb (unrounded). */
export const unitToLb = (value: number, unit: WeightUnit): number => (unit === FHIR_UNITS.KG ? kgToLb(value) : value);

/**
 * A FHIR weight quantity in the display unit, or undefined when it has no
 * value. A reading already stored in the display unit is returned as stored:
 * converting it through canonical lb would round it (70 kg → 154.32 lb →
 * 69.9985 kg). Everything else goes through readingToLb, including its lb
 * fallback.
 */
export const readingToUnit = (quantity: WeightQuantity | undefined, unit: WeightUnit): number | undefined => {
  const lb = readingToLb(quantity);
  if (lb === undefined || quantity?.value == null) {
    return undefined;
  }
  if (storedWeightUnit(quantity) === unit) {
    return quantity.value;
  }
  return lbToUnit(lb, unit);
};

/**
 * Decimal places a converted threshold is stored with in lb: fhir-core's
 * canonical precision, which is also what the patient app stores.
 */
const STORED_LB_DECIMALS = 2;

/**
 * One lb threshold in the display unit, rounded to the form's precision for
 * that unit. Stored lb values usually have one decimal and come back as they
 * are; a two-decimal value stored by a kg user rounds to one decimal here, so
 * the lb form can show and validate it.
 */
const thresholdToDisplay = (lb: number | undefined, unit: WeightUnit): number | undefined => {
  if (lb === undefined) {
    return undefined;
  }
  return roundTo(lbToUnit(lb, unit), WEIGHT_THRESHOLD_DECIMALS[unit]);
};

/** Convert lb weight thresholds to the display unit. */
export const weightParamsToDisplay = (lb: WeightParameters, unit: WeightUnit): WeightParameters => ({
  minimum: thresholdToDisplay(lb.minimum, unit),
  maximum: thresholdToDisplay(lb.maximum, unit),
  maxDelta: thresholdToDisplay(lb.maxDelta, unit),
});

/**
 * Convert display-unit weight thresholds back to lb for saving. A field whose
 * displayed value did not change keeps its original lb value, so saving one
 * field never drifts the others through a kg round trip or a display rounding.
 * Changed values are stored to two decimals (fhir-core's canonical precision):
 * a one-decimal lb entry is stored as typed, and a kg entry reads back as the
 * same two-decimal kg value, since 0.005 lb of rounding is under 0.0025 kg.
 */
export const weightParamsFromDisplay = (
  display: WeightParameters,
  originalLb: WeightParameters,
  unit: WeightUnit
): WeightParameters => {
  const fromDisplay = (value: number | undefined, original: number | undefined): number | undefined => {
    if (value === undefined) {
      return undefined;
    }
    if (original !== undefined && thresholdToDisplay(original, unit) === value) {
      return original;
    }
    return roundTo(unitToLb(value, unit), STORED_LB_DECIMALS);
  };
  return {
    minimum: fromDisplay(display.minimum, originalLb.minimum),
    maximum: fromDisplay(display.maximum, originalLb.maximum),
    maxDelta: fromDisplay(display.maxDelta, originalLb.maxDelta),
  };
};
